import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays, ChevronDown, ChevronUp, CreditCard, Loader2, RefreshCw, Trash2, Users, Wallet } from 'lucide-react'
import api from '../../utils/api'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatAmount(value) {
  const amount = Number(value || 0)
  return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatPercent(value) {
  const percent = Number(value || 0)
  return `${Number.isFinite(percent) ? percent.toFixed(2) : '0.00'}%`
}

function formatRechargePair(firstValue, secondValue) {
  return `${Number(firstValue || 0)}/${Number(secondValue || 0)}`
}

export default function ChannelDetailTab() {
  const [channelOptions, setChannelOptions] = useState([])
  const [channelName, setChannelName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [data, setData] = useState(null)
  const [newUserExpanded, setNewUserExpanded] = useState(true)
  const [rechargeExpanded, setRechargeExpanded] = useState(true)
  const [uninstallExpanded, setUninstallExpanded] = useState(true)

  useEffect(() => {
    fetchOptions()
  }, [])

  const fetchOptions = async () => {
    setOptionsLoading(true)
    try {
      const response = await api.get('/admin/channel-detail-options')
      if (response.data.success) {
        const d = response.data.data || response.data || {}
        const channels = d.channels || []
        setChannelOptions(channels)
        // 默认选中第一个渠道并自动查询
        if (channels.length > 0 && !channelName) {
          setChannelName(channels[0])
          fetchDataByChannel(channels[0])
        }
      }
    } catch (error) {
      console.error('获取渠道选项失败:', error)
    } finally {
      setOptionsLoading(false)
    }
  }

  const fetchDataByChannel = async (name) => {
    if (!name) return
    setLoading(true)
    try {
      const response = await api.get('/admin/channel-detail-stats', {
        params: {
          channel_name: name,
          start_date: startDate || undefined,
          end_date: endDate || undefined
        }
      })
      if (response.data.success) {
        setData(response.data.data || response.data || null)
      }
    } catch (error) {
      console.error('获取渠道详情失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchData = async () => {
    if (!channelName) return
    setLoading(true)
    try {
      const response = await api.get('/admin/channel-detail-stats', {
        params: {
          channel_name: channelName,
          start_date: startDate || undefined,
          end_date: endDate || undefined
        }
      })
      if (response.data.success) {
        setData(response.data.data || response.data || null)
      }
    } catch (error) {
      console.error('获取渠道详情失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    fetchData()
  }

  const handleReset = () => {
    setStartDate('')
    setEndDate('')
    setData(null)
  }

  const overview = data?.overview || {}
  const conversion = data?.conversion || {}
  const newUserDaily = data?.new_user_daily || []
  const rechargeDaily = data?.recharge_daily || []
  const rechargeSummary = data?.recharge_summary || {}
  const uninstallDaily = data?.uninstall_daily || []
  const uninstallSummary = data?.uninstall_summary || {}

  // 新增用户按日汇总（合并所有 label 为每日合计）
  const newUserDailyMerged = useMemo(() => {
    const dayMap = new Map()
    for (const item of newUserDaily) {
      const day = String(item.stat_date || '').trim()
      if (!day) continue
      if (!dayMap.has(day)) {
        dayMap.set(day, { stat_date: day, new_user_count: 0, used_user_count: 0, delayed_first_use_count: 0 })
      }
      const current = dayMap.get(day)
      current.new_user_count += Number(item.new_user_count || 0)
      current.used_user_count += Number(item.used_user_count || 0)
      current.delayed_first_use_count += Number(item.delayed_first_use_count || 0)
    }
    return Array.from(dayMap.values()).sort((a, b) => b.stat_date.localeCompare(a.stat_date))
  }, [newUserDaily])

  // 充值报表按日汇总
  const rechargeDailyMerged = useMemo(() => {
    const dayMap = new Map()
    for (const item of rechargeDaily) {
      const day = String(item.stat_date || '').trim()
      if (!day) continue
      if (!dayMap.has(day)) {
        dayMap.set(day, {
          stat_date: day, registered_count: 0, used_user_count: 0, recharge_user_count: 0,
          recharge_total_amount: 0,
          first_recharge_user_count_1_3d: 0, second_recharge_user_count_1_3d: 0,
          first_recharge_user_count_1_7d: 0, second_recharge_user_count_1_7d: 0,
          first_recharge_user_count_1_15d: 0, second_recharge_user_count_1_15d: 0,
          first_recharge_user_count_1_30d: 0, second_recharge_user_count_1_30d: 0
        })
      }
      const current = dayMap.get(day)
      current.registered_count += Number(item.registered_count || 0)
      current.used_user_count += Number(item.used_user_count || 0)
      current.recharge_user_count += Number(item.recharge_user_count || 0)
      current.recharge_total_amount += Number(item.recharge_total_amount || 0)
      current.first_recharge_user_count_1_3d += Number(item.first_recharge_user_count_1_3d || 0)
      current.second_recharge_user_count_1_3d += Number(item.second_recharge_user_count_1_3d || 0)
      current.first_recharge_user_count_1_7d += Number(item.first_recharge_user_count_1_7d || 0)
      current.second_recharge_user_count_1_7d += Number(item.second_recharge_user_count_1_7d || 0)
      current.first_recharge_user_count_1_15d += Number(item.first_recharge_user_count_1_15d || 0)
      current.second_recharge_user_count_1_15d += Number(item.second_recharge_user_count_1_15d || 0)
      current.first_recharge_user_count_1_30d += Number(item.first_recharge_user_count_1_30d || 0)
      current.second_recharge_user_count_1_30d += Number(item.second_recharge_user_count_1_30d || 0)
    }
    return Array.from(dayMap.values()).sort((a, b) => b.stat_date.localeCompare(a.stat_date))
  }, [rechargeDaily])

  // 卸载按日汇总
  const uninstallDailyMerged = useMemo(() => {
    const dayMap = new Map()
    for (const item of uninstallDaily) {
      const day = String(item.stat_date || '').trim()
      if (!day) continue
      if (!dayMap.has(day)) {
        dayMap.set(day, { stat_date: day, uninstall_count: 0 })
      }
      dayMap.get(day).uninstall_count += Number(item.uninstall_count || 0)
    }
    return Array.from(dayMap.values()).sort((a, b) => b.stat_date.localeCompare(a.stat_date))
  }, [uninstallDaily])

  return (
    <div className="space-y-6">
      {/* 标题和筛选 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">渠道详情</h2>
          <p className="mt-1 text-sm text-gray-500">选择单一渠道查看其所有统计信息（数据截止到前一天）</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <select
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
            disabled={optionsLoading}
          >
            <option value="">{optionsLoading ? '加载中...' : '请选择渠道'}</option>
            {channelOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>
          <span className="text-sm text-gray-400">至</span>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <button type="submit" disabled={!channelName} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            重置
          </button>
          <button
            type="button"
            onClick={fetchData}
            disabled={!channelName || loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </form>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* 日期区间提示 */}
          <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
            渠道：{data.channel_name}，统计区间：{data.start_date} 至 {data.end_date}，匹配标签数：{(data.matched_labels || []).length}
          </div>

          {/* 核心指标卡片 */}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">新增用户</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-blue-600">
                <Users className="h-5 w-5" />{overview.new_user_count || 0}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">已使用用户</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-emerald-600">
                <Users className="h-5 w-5" />{overview.used_user_count || 0}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">使用率</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-cyan-600">
                <BarChart3 className="h-5 w-5" />{formatPercent(overview.usage_rate)}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">充值人数</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-orange-600">
                <Wallet className="h-5 w-5" />{overview.recharge_user_count || 0}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">充值率</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-purple-600">
                <BarChart3 className="h-5 w-5" />{formatPercent(overview.recharge_rate)}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">充值总金额</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-fuchsia-600">
                <CreditCard className="h-5 w-5" />{formatAmount(overview.recharge_amount)}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">卸载数</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-red-600">
                <Trash2 className="h-5 w-5" />{overview.uninstall_count || 0}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-gray-500">付费人均金额</div>
              <div className="mt-2 text-2xl font-bold text-rose-600">{formatAmount(overview.avg_paid_amount)}</div>
            </div>
          </div>

          {/* 转化统计 */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">转化统计</h3>
            <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <div className="text-xs text-gray-500">使用率</div>
                <div className="mt-1 text-lg font-bold text-emerald-600">{formatPercent(conversion.usage_rate)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <div className="text-xs text-gray-500">免费用户使用率</div>
                <div className="mt-1 text-lg font-bold text-cyan-600">{formatPercent(conversion.free_usage_rate)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <div className="text-xs text-gray-500">充值率</div>
                <div className="mt-1 text-lg font-bold text-orange-600">{formatPercent(conversion.recharge_rate)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <div className="text-xs text-gray-500">二次付费占比</div>
                <div className="mt-1 text-lg font-bold text-purple-600">{formatPercent(conversion.second_paid_rate)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <div className="text-xs text-gray-500">3次以上付费占比</div>
                <div className="mt-1 text-lg font-bold text-rose-600">{formatPercent(conversion.three_plus_paid_rate)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <div className="text-xs text-gray-500">充值订单数</div>
                <div className="mt-1 text-lg font-bold text-gray-900">{conversion.recharge_order_count || 0}</div>
              </div>
            </div>
          </div>

          {/* 新增用户按日明细 */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">新增用户统计（按日）</h3>
              <button
                type="button"
                onClick={() => setNewUserExpanded(!newUserExpanded)}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                {newUserExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {newUserExpanded ? '收缩' : '展开'}
              </button>
            </div>
            {newUserExpanded && <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">新增用户数</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">已使用用户数</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">后续首次使用</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">未使用用户数</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {newUserDailyMerged.length > 0 ? newUserDailyMerged.map((item) => (
                    <tr key={item.stat_date} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{item.stat_date}</td>
                      <td className="px-6 py-3 text-right text-sm font-medium text-blue-600">{item.new_user_count}</td>
                      <td className="px-6 py-3 text-right text-sm text-emerald-600">{item.used_user_count}</td>
                      <td className="px-6 py-3 text-right text-sm text-cyan-600">{item.delayed_first_use_count}</td>
                      <td className="px-6 py-3 text-right text-sm text-amber-600">{Math.max(0, item.new_user_count - item.used_user_count)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">暂无数据</td></tr>
                  )}
                </tbody>
              </table>
            </div>}
          </div>

          {/* 充值报表按日明细 */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">充值报表（按日）</h3>
              <button
                type="button"
                onClick={() => setRechargeExpanded(!rechargeExpanded)}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                {rechargeExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {rechargeExpanded ? '收缩' : '展开'}
              </button>
            </div>
            {rechargeExpanded && <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">注册人数</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">使用人数</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">注册日首充</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">充值总金额</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">3天首充/二充</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">7天首充/二充</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">15天首充/二充</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">30天首充/二充</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rechargeDailyMerged.length > 0 ? rechargeDailyMerged.map((item) => (
                    <tr key={item.stat_date} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.stat_date}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-blue-600">{item.registered_count}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{item.used_user_count}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-orange-600">{item.recharge_user_count}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-fuchsia-600">{formatAmount(item.recharge_total_amount)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_3d, item.second_recharge_user_count_1_3d)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_7d, item.second_recharge_user_count_1_7d)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_15d, item.second_recharge_user_count_1_15d)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_30d, item.second_recharge_user_count_1_30d)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={9} className="px-6 py-8 text-center text-sm text-gray-500">暂无数据</td></tr>
                  )}
                </tbody>
              </table>
            </div>}
          </div>

          {/* 卸载统计按日明细 */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">卸载统计（按日）</h3>
              <button
                type="button"
                onClick={() => setUninstallExpanded(!uninstallExpanded)}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                {uninstallExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {uninstallExpanded ? '收缩' : '展开'}
              </button>
            </div>
            {uninstallExpanded && <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">卸载数量</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {uninstallDailyMerged.length > 0 ? uninstallDailyMerged.map((item) => (
                    <tr key={item.stat_date} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{item.stat_date}</td>
                      <td className="px-6 py-3 text-right text-sm font-medium text-red-600">{item.uninstall_count}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={2} className="px-6 py-8 text-center text-sm text-gray-500">暂无数据</td></tr>
                  )}
                </tbody>
              </table>
            </div>}
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-16 text-center text-sm text-gray-400 shadow-sm">
          请选择渠道后点击查询
        </div>
      )}
    </div>
  )
}
